import { useNavigate } from "react-router-dom";
import { FerramanetasDetalhe } from "../../shared/components/ferramentasDaListagem/ferramentas-detalhe";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { useMutation } from "@tanstack/react-query";
import {
  useTheme,
  Box,
  useMediaQuery,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { CriarObraFormData, criarObraSchema } from "./schema/criar-obra.schema";
import { CriarObraContract } from "./contracts/criar-obra.contract";
import { EnderecoFormPart } from "../../shared/components/formulario/parts/EnderecoFormPart/enderecoform.part";
import Input from "../../shared/components/formulario/input/input";
import InputMascarado from "../../shared/components/formulario/input-mascarado/InputMascarado";
import { mascaraMoedaReal } from "../../shared/utils/mascaras/mascaraMoedaReal";
import InputData from "../../shared/components/formulario/input-data/inputData";
import InputDescricao from "../../shared/components/formulario/input-descricao/inputDescricao";
import { SelectCliente } from "../../shared/components/formulario/input-select-cliente/selectCliente";
import { obraService } from "./service/obra.service";
import { toast } from "react-toastify";
import useLoadingStore from "../../shared/store/loading.store";
import { toastError } from "../../shared/services/errors/erros-handler";
import { InputFileUpload } from "../../shared/components/formulario/input-file/inputFile";
import { useState } from "react";
import { EtapasDaObraFormPart } from "../../shared/components/formulario/parts/EtapasDaFormPart/etapasdaObraform.part";
import { CargosFormPart } from "../../shared/components/formulario/parts/CargosPrecoDaFormPart /CargosPrecoObraform.part";

const PAGINAS = [
  { label: "Dados básicos", value: 0 },
  { label: "Etapas da obra", value: 1 },
  { label: "Cargos e preços", value: 2 },
  // Futuras páginas podem ser adicionadas aqui
];

export function CriarObra() {
  const methods = useForm<CriarObraContract>({
    resolver: yupResolver(criarObraSchema),
    defaultValues: {
      workStages: [],
    },
  });

  const { setLoading } = useLoadingStore();
  const [obraCriada, setObraCriada] = useState<any>(null);
  const [step, setStep] = useState(0);

  const { isPending, mutate } = useMutation({
    mutationFn: async (data: CriarObraFormData) => {
      setLoading(true, "Estamos quase lá! Só mais um momento...");
      const result = await obraService.criar(data);
      setObraCriada(result);
      return result;
    },
    onSuccess: (result) => {
      toast.success(
        "Obra criada com sucesso!"
      );
      navigate("/obras")
    },
    onError: (err: any) => {
      toastError(err);
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = methods;
  const navigate = useNavigate();
  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <BaseLayout title="Criar Obra">
      <FerramanetasDetalhe
        mostraBotaoApagar={false}
        mostraBotaoNovo={false}
        mostraBotaoSalvarEVoltar={false}
        mostraBotaoVoltar
        aoClicarBotaoSalvar={handleSubmit((formData) => mutate(formData))}
        aoClicarBotaoVoltar={() => navigate("/obras")}
      />

      {/* Navegação moderna usando Tabs */}
      <Tabs
        value={step}
        onChange={(_, newValue) => setStep(newValue)}
        sx={{
          borderBottom: "1px solid #eee",
          mb: 2,
          ".MuiTab-root": { textTransform: "none", fontWeight: 500 },
        }}
        variant={mdDown ? "scrollable" : "standard"}
        scrollButtons={mdDown ? "auto" : false}
      >
        {PAGINAS.map((pagina) => (
          <Tab key={pagina.value} label={pagina.label} value={pagina.value} />
        ))}
      </Tabs>

      <Box
        component="form"
        onSubmit={handleSubmit((formData) => mutate(formData))}
        padding="20px 0"
        display="flex"
        flexDirection="column"
        height="calc(100% - 100px )"
      >
        {step === 0 && (
          <>
            <Box
              display="grid"
              gridTemplateColumns={mdDown ? "1fr" : "1fr 1fr"}
              gap="20px"
              width="100%"
              flexWrap="wrap"
            >
              <Input
                name="workName"
                label="Nome da Obra"
                methods={methods}
                required
              />

              <Controller
                name="idClient"
                control={control}
                render={({ field }) => (
                  <SelectCliente
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </Box>

            <Box
              display="grid"
              gridTemplateColumns={mdDown ? "1fr" : "1fr 1fr 1fr"}
              gap="20px"
              width="100%"
              flexWrap="wrap"
            >
              <InputMascarado
                name="totalValue"
                methods={methods}
                label="Valor Total"
                mask={mascaraMoedaReal}
              />
              <InputData
                name="startDate"
                label="Data de Início"
                methods={methods}
              />
              <InputData
                name="endDate"
                label="Data de Término"
                methods={methods}
              />
            </Box>

            <EnderecoFormPart methods={methods} mdDown={mdDown} />

            <InputDescricao
              name="description"
              methods={methods}
              label="Descrição"
            />

            <InputFileUpload
              name="contractFile"
              label="Adicione um contrato nos seguintes formatos (PDF, JPG ou PNG)"
              methods={methods}
              required={true}
            />
          </>
        )}

        {step === 1 && (
          <Box>
            <EtapasDaObraFormPart methods={methods} />
          </Box>
        )}

        {step === 2 && (
          <Box>
            <CargosFormPart methods={methods} />
          </Box>
        )}
      </Box>
    </BaseLayout>
  );
}
